import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spin, Table, Form, Modal, message, Breadcrumb, Result } from "antd";
import get from "lodash/get";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import includes from "lodash/includes";
import trim from "lodash/trim";
import toLower from "lodash/toLower";
import { SettingOutlined, ContainerOutlined } from "@ant-design/icons";
import { SUPER_ADMIN_ROLE_ACCESS, ORGANISATION_ADMIN_ROLE_IDs, ORGANISATION_MANAGER_IDs } from "../../constants/userRoles";
import { partColumns } from "../../constants/tableColumns/part";
import {
  fetchCompanies,
  fetchCompanyParts,
  deleteCompanyParts,
} from "../../redux/actions/companyActions";
import PartForm from "../../components/part/partForm";
import { DELETE_PART_SUCCESS } from "../../constants/message";
import requireAuth from "../../hocs/requireAuth";
import HeaderSelector from "../../components/part/headerSelector";
import SearchPart from "../../components/part/searchPart";

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <SettingOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <ContainerOutlined style={{ fontSize: "16px" }} />
              <span>Part</span>
            </>
          ),
        },
        {
          title: "Settings",
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
  templateType: undefined,
};

const PartSettingScreen = () => {
  const [loading, setLoading] = useState(false);
  const isStaff = useSelector(state => get(state, "auth.isStaff"))
  const [form] = Form.useForm();
  const [filter, setFilters] = useState(initialState);
  const [config, setConfig] = useState(undefined);
  const [showEmpty, setShowEmpty] = useState(false)
  const [parts, setParts] = useState({
    show: false,
    partsArr: [],
    displayedParts: [],
  });
  const dispatch = useDispatch();
  const [partModal, setPartModal] = useState({
    visible: false,
    data: undefined,
  });

  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });
    setConfig({
      ...config,
      companies: [company],
      template_types: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      template_type: undefined,
      Company: get(company, "id"),
    });
  };

  const togglePartModal = (data = undefined) => {
    setPartModal({
      visible: !get(partModal, "visible"),
      data,
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (get(result, "length") === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getParts = () => {
    const query = { type: get(filter, "templateType") };
    setLoading(true);
    dispatch(fetchCompanyParts(get(filter, "company.id"), query))
      .then((result) => {
        if (isEmpty(result)) {
          setShowEmpty(true)
        }
        else {
          setShowEmpty(false)
        }
        setLoading(false);
        setParts({ show: true, partsArr: result, displayedParts: result });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDelete = (part) => {
    setLoading(true);
    dispatch(deleteCompanyParts(get(part, "id"), get(filter, "company.id")))
      .then(() => {
        getParts();
        message.success(DELETE_PART_SUCCESS);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onSearchPart = debounce((q) => {
    setParts({
      ...parts,
      displayedParts: get(parts, "partsArr").filter((part) =>
        includes(toLower(trim(get(part, "name"))), q)
      ),
    });
  }, 300);

  useEffect(() => {
    getCompanies();
  }, []);
  return (
    <div className="pl-2 py-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <div>
          <PageBreadcrumb />
        </div>
        <div></div>
      </div>
      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full">
        <Spin spinning={loading}>
          <Form form={form} className="w-full px-8 pt-6">
            <HeaderSelector
              getParts={() => {
                getParts();
              }}
              filter={filter}
              setConfig={setConfig}
              setFilters={setFilters}
              form={form}
              config={config}
              setParts={setParts}
            />
          </Form>
          {get(parts, "show") && (
            <div className={`px-8  overflow-y-auto`}>
              <Spin spinning={loading}>
                <SearchPart
                  onSearchPart={onSearchPart}
                  getParts={getParts}
                  togglePartModal={togglePartModal}
                  parts={parts}
                />
                {get(parts, "displayedParts.length") > 0 && (
                  <Table
                    className="antd-table-xs"
                    columns={partColumns(
                      togglePartModal,
                      onDelete,
                      get(filter, "templateType"),
                      isStaff,
                    )}
                    dataSource={get(parts, "displayedParts")}
                    scroll={{ x: "max-content" }}
                    rowKey={(record) =>
                      get(record, "id") || crypto.randomUUID()
                    }
                    pagination={{
                      pageSize: 20
                    }}
                  />
                )}
              </Spin>
            </div>
          )}
          {showEmpty && (
            <div className="flex flex-wrap items-center justify-center text-white h-[100%]">
              <div className="px-8 py-8">
                <Result
                  title="No parts exits."
                  subTitle="Please try again with another company or template type."
                  status="404"
                />
              </div>
            </div>
          )}
          <Modal
            title={get(partModal, "data", undefined) ? "Edit Part" : "Add Part"}
            open={get(partModal, "visible")}
            destroyOnHidden
            footer={false}
            onOk={() => { }}
            onCancel={() => {
              togglePartModal();
            }}
            width="70%"
            height="80vh"
          >
            <PartForm
              toggle={() => {
                togglePartModal();
              }}
              initialValue={
                get(partModal, "data")
                  ? get(partModal, "data")
                  : {
                    meta: {
                      costings: {
                        job_cost_per_pc: 0,
                        profit_per_pc: 0,
                        contractual_cost_per_pc: 0,
                      },
                    },
                  }
              }
              filter={filter}
              onSuccess={() => {
                togglePartModal();
                getParts();
              }}
            />
          </Modal>
        </Spin>
      </div>
    </div>
  );
};
export default requireAuth(PartSettingScreen, [
  ...SUPER_ADMIN_ROLE_ACCESS,
  ...ORGANISATION_ADMIN_ROLE_IDs,
  ...ORGANISATION_MANAGER_IDs
]);
